import { env } from "@/config/env";
import { handleAuthError as classifyAuthError, formatAuthError } from "@/lib/authErrorHandler";
import { resolveAssistedCareRoute } from "@/lib/featureFlags";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../types/supabase-extended";

// Lazy initialization of Supabase client
let _supabase: SupabaseClient<Database> | null = null;

function getSupabaseClient() {
  if (_supabase !== null) {
    return _supabase;
  }

  // Environment validation
  const supabaseUrl = env.VITE_SUPABASE_URL;
  const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error("Missing VITE_SUPABASE_URL environment variable");
  }

  if (!supabaseAnonKey) {
    throw new Error("Missing VITE_SUPABASE_ANON_KEY environment variable");
  }

  // Initialize the Supabase client with unified configuration
  _supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // Enable automatic OAuth token detection
      storageKey: "backon-auth",
      storage: window.localStorage,
    },
  });

  return _supabase;
}

// Export the lazy-loaded supabase client
export const supabase: SupabaseClient<Database> = new Proxy(
  {} as SupabaseClient<Database>,
  {
    get(target, prop) {
      if (typeof prop === "string") {
        return (getSupabaseClient() as any)[prop];
      }
      return undefined;
    },
  },
) as unknown as SupabaseClient<Database>;

// Export commonly used auth functions
export const signUp = async (email: string, password: string) => {
  return await getSupabaseClient().auth.signUp({ email, password });
};

export const signIn = async (email: string, password: string) => {
  return await getSupabaseClient().auth.signInWithPassword({ email, password });
};

export const updatePassword = async (password: string) => {
  return await getSupabaseClient().auth.updateUser({ password });
};

export const onAuthStateChange = (
  callback: (event: string, session: any) => void,
) => {
  return getSupabaseClient().auth.onAuthStateChange(callback);
};

// Error handler for Supabase operations
export const handleSupabaseError = (error: any) => {
  console.error("Supabase error:", error);
  return error.message || "An unexpected error occurred.";
};

// Utility functions for OAuth authentication
export const signInWithGoogle = async () => {

  const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
      },
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

// Listen for authentication state changes
getSupabaseClient().auth.onAuthStateChange((event) => {
  if (import.meta.env.DEV) console.info("Auth state changed:", event);

  if (event === "SIGNED_IN") {
    if (import.meta.env.DEV) console.info("User signed in");
    // The user will be redirected by the auth callback
  } else if (event === "SIGNED_OUT") {
    console.log("👋 User signed out");
  } else if (event === "TOKEN_REFRESHED") {
    console.log("🔄 Token refreshed");
  }
});

// Utility function to get user profile
export const getUserProfile = async (userId?: string) => {
  const {
    data: { user },
  } = await getSupabaseClient().auth.getUser();
  const targetUserId = userId || user?.id;

  if (!targetUserId) return { data: null, error: new Error("No user ID") };

  const { data, error } = await getSupabaseClient()
    .from("profiles")
    .select("*")
    .eq("id", targetUserId)
    .single();

  return { data, error };
};

// Role management functions
export const updateUserRole = async (
  role: "assisted_care" | "family" | "individual" | "business",
) => {
  const {
    data: { user },
  } = await getSupabaseClient().auth.getUser();

  if (!user) return { data: null, error: new Error("No authenticated user") };

  const { data, error } = await getSupabaseClient()
    .from("profiles")
    .update({ app_role: role })
    .eq("id", user.id)
    .select()
    .single();

  return { data, error };
};

export const getRoleBasedRedirect = (role: string | null) => {
  switch (role) {
    case "assisted_care":
      return resolveAssistedCareRoute("/assisted-care");
    case "family":
      return "/family/dashboard";
    case "individual":
      return "/individual/dashboard";
    case "business":
      return "/business/dashboard";
    default:
      return "/onboarding/role";
  }
};

// Utility function to update user profile
export const updateUserProfile = async (userId: string, updates: any) => {
  const { data, error } = await getSupabaseClient()
    .from("profiles")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", userId)
    .select()
    .single();

  if (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }

  return data;
};

// Connection health check
export const checkConnection = async () => {
  try {
    const { data, error } = await getSupabaseClient()
      .from("profiles")
      .select("count")
      .limit(1);
    return { healthy: !error, error };
  } catch (err) {
    return { healthy: false, error: err };
  }
};

// Safe operation wrapper
export const safeOperation = async <T>(
  operation: () => Promise<T>,
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    console.error("Operation failed:", error);
    throw error;
  }
};

// Compatibility adapter: one safe, code-aware error map for all sign-in views.
export const handleAuthError = (error: any, email?: string): string => {
  const info = classifyAuthError(error);
  const message = formatAuthError(info);
  if (info.type === 'auth_email_not_confirmed' && email && isZohoEmail(email)) {
    return message + ' Zoho users: also check your spam folder.';
  }
  return message;
};

// Helper function to detect Zoho emails
export const isZohoEmail = (email: string): boolean => {
  const zohoPatterns = [
    "@zoho.com",
    "@zoho.in",
    "@zoho.eu",
    "@zohomail.com",
    // Add more Zoho patterns as needed
  ];
  const emailLower = email.toLowerCase();
  return (
    zohoPatterns.some((pattern) => emailLower.includes(pattern)) ||
    emailLower.includes("zoho")
  ); // Catch custom domains using Zoho
};

// Enhanced signup function with Zoho-specific handling
export const signUpWithZohoHandling = async (
  email: string,
  password: string,
) => {
  const result = await getSupabaseClient().auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (result.error) {
    throw new Error(handleAuthError(result.error, email));
  }

  // If user uses Zoho email, provide additional guidance
  if (
    isZohoEmail(email) &&
    result.data.user &&
    !result.data.user.email_confirmed_at
  ) {
    return {
      ...result,
      isZohoEmail: true,
      message:
        "Account created! Zoho users: Please check your spam folder and add no-reply@backongoal.com to your contacts.",
    };
  }

  return result;
};

// Enhanced authentication utilities for Golden Path
export const getCurrentUserWithOrg = async () => {
  try {
    const { data: { user } } = await getSupabaseClient().auth.getUser();
    
    if (!user) return null;

    const { data: profile } = await getSupabaseClient()
      .from("profiles")
      .select("org_id, role, stream, display_name")
      .eq("id", user.id)
      .single();

    return {
      id: user.id,
      email: user.email || "",
      org_id: profile?.org_id,
      role: profile?.role,
      stream: profile?.stream,
      display_name: profile?.display_name,
    };
  } catch (error) {
    console.error("❌ Auth: Error getting current user:", error);
    return null;
  }
};

// Validate JWT claims for RLS policies
export const validateJWTClaims = async () => {
  try {
    const { data: { session } } = await getSupabaseClient().auth.getSession();
    
    if (!session?.user) {
      return { valid: false, error: "No active session" };
    }

    const user = await getCurrentUserWithOrg();
    
    if (!user?.org_id) {
      return { valid: false, error: "User missing org_id" };
    }

    return { 
      valid: true, 
      user,
      claims: {
        sub: user.id,
        org_id: user.org_id,
        role: user.role,
        stream: user.stream,
      }
    };
  } catch (error) {
    console.error("❌ Auth: JWT validation error:", error);
    return { valid: false, error: error.message };
  }
};
